import { DateTime } from "luxon";

import { LabResultObservation } from "../Models/CareElements/LabResultObservation";
import { LabResultsCareElement } from "../Models/CareElements/LabResultsCareElement";

type ObservationWithCareElement = {
    observation: LabResultObservation;
    careElement: LabResultsCareElement;
    date: Date;
};

type DateLabObservations = Record<string, Record<string, { observation: LabResultObservation, careElement: LabResultsCareElement }[]>>;

class LabResultsColumnHelper {
    /**
     * Main method to process care elements and return column data
     */
    public static processLabResultsColumns(filteredCareElements: LabResultsCareElement[]): { dates: Date[], dateLabObservations: DateLabObservations } {
        const allObservations = this.flattenObservations(filteredCareElements);
        const dateLabObservations = this.groupObservationsByDateAndLab(allObservations);
        const dates = this.createRepeatedDates(dateLabObservations);

        return { dates, dateLabObservations };
    }

    /**
     * Gets the observation for a specific column
     */
    public static getObservationForColumn(
        dateLabObservations: DateLabObservations,
        dates: Date[],
        date: Date,
        labCode: string,
        columnIndex: number
    ): { observation: LabResultObservation, careElement: LabResultsCareElement } | undefined {
        const firstOccurrenceIndex = dates.findIndex(d => d.getTime() === date.getTime());
        const occurrenceIndex = columnIndex - firstOccurrenceIndex;
        const dateKey = date.toISOString();
        const labObservations = dateLabObservations[dateKey]?.[labCode];
        return labObservations?.[occurrenceIndex];
    }

    /**
     * Flattens care elements into observations with metadata
     */
    private static flattenObservations(careElements: LabResultsCareElement[]): ObservationWithCareElement[] {
        return careElements
            .flatMap(careElement =>
                careElement.Observations.map(observation => ({
                    observation,
                    careElement,
                    date: DateTime.fromISO(observation.Date).startOf("day").toJSDate()
                })))
            .sort((a, b) => b.date.getTime() - a.date.getTime());
    }

    /**
     * Groups observations by date and lab type
     */
    private static groupObservationsByDateAndLab(allObservations: ObservationWithCareElement[]): DateLabObservations {
        const dateLabObservations: DateLabObservations = {};

        allObservations.forEach(item => {
            const dateKey = item.date.toISOString();
            const labCode = item.careElement.AnalysisStandardCode;

            if (!dateLabObservations[dateKey]) {
                dateLabObservations[dateKey] = {};
            }

            if (!dateLabObservations[dateKey][labCode]) {
                dateLabObservations[dateKey][labCode] = [];
            }

            dateLabObservations[dateKey][labCode].push({
                observation: item.observation,
                careElement: item.careElement
            });
        });

        return dateLabObservations;
    }

    /**
     * Creates dates array with repetitions based on max lab count per date
     */
    private static createRepeatedDates(dateLabObservations: DateLabObservations): Date[] {
        const dates: Date[] = [];

        Object.entries(dateLabObservations)
            .sort(([dateA], [dateB]) => new Date(dateB).getTime() - new Date(dateA).getTime())
            .forEach(([dateKey, labObservations]) => {
                const maxCount = Math.max(...Object.values(labObservations).map(obs => obs.length));
                const date = new Date(dateKey);

                for (let i = 0; i < maxCount; i++) {
                    dates.push(date);
                }
            });

        return dates;
    }
}

export { LabResultsColumnHelper };
